import { Html } from "@react-three/drei";
import { Fragment, useEffect, useState } from "react";
import { useStore } from "../Store/zustandStore";
import {
  calculateBounds,
  fromMM,
  getFramesFromScene,
  toMM,
  prltcDebugLog,
} from "../Utils/frameUtils";
import {
  collectDimensionables,
  calculateDimensions,
} from "../Utils/dimensionUtils";
import { COMPONENT_ALIGNMENT } from "../Constants";

export default function Dimensions({ frames, enabled, dimensionSide }) {
  const [frameThickness] = useStore((state) => [state.frameThickness]);
  const sectionIds = !frames
    ? []
    : [
        ...new Set(
          frames.filter((f) => f.sectionId != null).map((f) => f.sectionId),
        ),
      ];

 // Mirror the 2D rule (createSVGDimensions): the unit cross-section height sits
  // on whichever END is NOT occupied by an external component, so it reads
  // against the in-frame body instead of crowding the external component.
  //   - external on the right end → unit height on the LEFT  (y1)
  //   - both ends external        → against the BODY left edge (y1, body frames)
  //   - otherwise                 → RIGHT (y2), as before
  // Detect the end components by ARRAY ORDER (frames are stored left→right),
  // not by position[0] — in the live 3D path positions can be unset at render
  // time, which made a position-sort collapse and mis-detect the ends.
  const orderedFrames = frames ?? [];
  const leftIsExterior = orderedFrames[0]?.isExterior === true;
  const rightIsExterior =
    orderedFrames[orderedFrames.length - 1]?.isExterior === true;
  const bothExterior = leftIsExterior && rightIsExterior;
  const unitHWLeft = rightIsExterior && !leftIsExterior;

  // TEMP DEBUG: confirm the parent-level end detection (the values that drive
  // height-side selection for all child Dimensions).
  if (typeof window !== "undefined" && window.__PRLTC_DEBUG__ === true) {
    prltcDebugLog({
      kind: "3d-dimensions-detect",
      leftIsExterior,
      rightIsExterior,
      bothExterior,
      unitHWLeft,
      frameOrder: orderedFrames.map((f) => ({
        id: f.id,
        isExterior: f.isExterior,
        posX: f.position?.[0],
      })),
    });
  }
  // Unit cross-section height side:
  //   - right-external only → left (y1)
  //   - both-external       → far left (y1) in 3D (drawn by unit-length over the
  //     full unit), and on the body's left edge / cone in 2D (drawn by the
  //     dedicated body-height callout). 3D-on-cone hid behind the geometry and
  //     looked bad, so 3D keeps it on the far left as before.
  //   - otherwise           → right (y2)
  const unitHeightAxis = unitHWLeft || bothExterior ? "y1" : "y2";
  const bodyFrames = !frames ? [] : frames.filter((f) => !f.isExterior);

   return enabled && frames?.length > 0 ? (
    <group userData={{ type: "dimensions" }}>
      <Dimension
        key="unit-length"
        debugKey="unit-length"
        frames={frames}
        allFrames={frames}
        offsetHorizontal={0.3}
        offsetVertical={0.25}
        frameThickness={frameThickness}
        dimensionSide={dimensionSide}
        baseFrameHeight={fromMM(100)}
        isTotal={true}
        sectionViews={{
          "3d": {
            // 3D draws the unit height on the far left (full unit) even when
            // both-external — the cone placement hid behind the 3D geometry.
            visibleAxes: ["x1", unitHeightAxis],
            levels: [2, 3, 1],
            alignments: {
              length: COMPONENT_ALIGNMENT.BOTTOM,
            },
          },
          "2d": {
            visibleAxes: ["x1"],
            levels: [2, 2],
            alignments: {
              length: COMPONENT_ALIGNMENT.BOTTOM,
            },
          },
        }}
      ></Dimension>
        {bothExterior && bodyFrames.length > 0 && (
        <Dimension
          key="body-height"
          debugKey="body-height"
          frames={bodyFrames}
          allFrames={bodyFrames}
          offsetHorizontal={0.3}
          offsetVertical={0.25}
          frameThickness={frameThickness}
          dimensionSide={dimensionSide}
          baseFrameHeight={fromMM(100)}
          isTotal={true}
          sectionViews={{
            // 2D ONLY: unit cross-section height against the body's left edge
            // (the ESP / left-external-component cone junction). 3D draws this on
            // the far left via unit-length instead (cone placement hid behind
            // the geometry), so the 3D axes are empty here.
            "3d": { visibleAxes: [], levels: [2, 2, 1] },
            "2d": { visibleAxes: ["y1"], levels: [1, 1, 1] },
          }}
        ></Dimension>
      )}
        <Dimension
          key="section-length"
          debugKey="section-length"
          frames={frames.filter((f) => !f.isExterior)}
          allFrames={frames}
          offsetHorizontal={0.1}
          offsetVertical={0.12}
          frameThickness={frameThickness}
          dimensionSide={dimensionSide}
          sectionViews={{
            "3d": {
              visibleAxes: frames.some((f) => f.isExterior)
                ? ["x1", unitHeightAxis]
                : [unitHeightAxis],
              levels: [1, 2, 1],
            },
            "2d": {
              visibleAxes: ["x1"],
              levels: [2, 3],
            },
          }}
        ></Dimension>
      )
      

      {frames
        .filter((f) => !f.sectionId)
        .map((f) => {
          //  return <Dimension key={f.id} frames={[f]} offsetHorizontal={0.25} offsetVertical={0.25} frameThickness={frameThickness} dimensionSide={dimensionSide}></Dimension>;
          return (
            <Dimension
              key={f.id}
              frames={[f]}
              allFrames={frames}
              frameThickness={frameThickness}
              dimensionSide={dimensionSide}
            ></Dimension>
          );
        })}
      {sectionIds.map((sectionId) => {
        return (
          <Dimension
            key={sectionId}
            frames={frames.filter((f) => f.sectionId == sectionId)}
            allFrames={frames}
            frameThickness={frameThickness}
            dimensionSide={dimensionSide}
            sectionViews={{
              "3d": {
                visibleAxes: ["x"],
                levels: [1, 1, 1],
              },
              "2d": {
                visibleAxes: ["x1"],
                levels: [2, 2],
              },
            }}
          ></Dimension>
          // <Dimension
          //   key={sectionId}
          //   frames={frames.filter((f) => f.sectionId == sectionId)}
          //   offsetHorizontal={0.25}
          //   offsetVertical={0.25}
          //   frameThickness={frameThickness}
          //   dimensionSide={dimensionSide}
          // ></Dimension>
        );
      })}
    </group>
  ) : null;
}

// function Dimension({ frames, frameThickness, dimensionSide, isTotal = false, offsetHorizontal = 0.25, offsetVertical = 0.25 }) {
//   const [positionAndSizes, setPositionAndSizes] = useState();
//   const multiplier = 200;

//   useEffect(() => {
//     if (!frames || frames.length == 0) return;
//     setPositionAndSizes(calculateDimensions(frames, frameThickness, offsetHorizontal, offsetVertical, dimensionSide, isTotal));
//   }, [frames, dimensionSide]);

//   return (
//     <group userData={{ type: "dimensions" }}>
//       {positionAndSizes
//         ?.filter((d) => d.lengthPosition || d.heightPosition || d.widthPosition)
//         .map((positionAndSize, i) => (
//           <Fragment key={`dimension-${i}`}>
//             {positionAndSize.showLength && (
//               <>
//                 <DimensionObjects position={positionAndSize.lengthPosition} rotation={[0, 0, Math.PI / 2]} length={positionAndSize.lengthSize} />
//                 <Html position={positionAndSize.lengthPosition} center>
//                   <div
//                     className="dimension-length"
//                     style={{
//                       width: positionAndSize.lengthSize * multiplier,
//                     }}
//                   >
//                     <div>
//                       <span>{positionAndSize.lengthSizeMM}</span>
//                     </div>
//                   </div>
//                 </Html>
//               </>
//             )}
//             {positionAndSize.showHeight && (
//               <>
//                 <DimensionObjects position={[ positionAndSize.heightPosition[0] * 1.1, positionAndSize.heightPosition[1],positionAndSize.heightPosition[2]]} rotation={[0, 0, 0]} length={positionAndSize.heightSize} />
//                 <Html position={[ positionAndSize.heightPosition[0] * 1.1, positionAndSize.heightPosition[1],positionAndSize.heightPosition[2]]} center>
//                   <div className="dimension-height" style={{ height: positionAndSize.heightSize * multiplier }}>
//                     <div>
//                       <span>{positionAndSize.heightSizeMM}</span>
//                     </div>
//                   </div>
//                 </Html>
//                 {i == 0 && (
//                   <>
//                     <DimensionObjects
//                       position={[positionAndSize.heightPosition[0], positionAndSize.heightPosition[1] - fromMM(50), positionAndSize.heightPosition[2]]}
//                       rotation={[0, 0, 0]}
//                       length={positionAndSize.heightSize + fromMM(100)}
//                     />
//                     <Html position={positionAndSize.heightPosition} center>
//                       <div className="dimension-height" style={{ height: (positionAndSize.heightSize + fromMM(100)) * multiplier }}>
//                         <div>
//                           <span>{positionAndSize.heightSizeMM + 100}</span>
//                         </div>
//                       </div>
//                     </Html>
//                   </>
//                 )}
//               </>
//             )}
//             {positionAndSize.showWidth && (
//               <>
//                 <DimensionObjects position={positionAndSize.widthPosition} rotation={[Math.PI / 2, 0, 0]} length={positionAndSize.widthSize} />
//                 <Html position={positionAndSize.widthPosition} rotation-y={-Math.PI / 2} center>
//                   <div className="dimension-width" style={{ width: positionAndSize.widthSize * multiplier, fontSize: "14px" }}>
//                     <div>
//                       <span>{positionAndSize.widthSizeMM}</span>
//                     </div>
//                   </div>
//                 </Html>
//               </>
//             )}
//           </Fragment>
//         ))}
//     </group>
//   );
// }

function DimensionAxis({ axis, data }) {
  const pos = data.positions[axis];
  const len = data[axis];
  const label = data.type != "text" ? data[`${axis}MM`] : data.text;
  const multiplier = 200;
  const rotationMap = {
    length: [0, 0, Math.PI / 2],
    height: [0, 0, 0],
    width: [Math.PI / 2, 0, 0],
  };

  return (
    <>
      {data.type != "text" ? (
        <>
          <DimensionObjects
            position={pos}
            rotation={rotationMap[axis]}
            length={len}
          />
          <Html position={pos} center>
            <div className={`dimension-${axis}`}>
              <div>
                <span>{label}</span>
              </div>
            </div>
          </Html>
        </>
      ) : (
        <Html position={pos} center>
          <div className={`dimension-label`}>
            <div>
              <span>{label}</span>
            </div>
          </div>
        </Html>
      )}
    </>
  );
}

function Dimension({
  frames,
  allFrames,
  frameThickness,
  dimensionSide,
  offsetHorizontal = 0,
  offsetVertical = 0,
  baseFrameHeight,
  isTotal,
  sectionViews,
  debugKey,
}) {
  const [dims, setDims] = useState([]);

  useEffect(() => {
    if (!frames || frames.length === 0) return;
    const dimensionables = collectDimensionables(      
      frames,
      allFrames,
      frameThickness,
      "3d",
      baseFrameHeight,
      isTotal,
      sectionViews,
      dimensionSide
    );

    const computed = calculateDimensions(
      dimensionables,
      dimensionSide,
      offsetHorizontal,
      offsetVertical,
    );
  setDims(computed);

    // TEMP DEBUG: capture 3D dimension placement (the live Dimensions.js path,
    // not the SVG path) to diagnose the unit-height side in 3D.
    if (debugKey) {
      prltcDebugLog({
        kind: `3d-${debugKey}`,
        view: "3d",
        dimensionSidePassed: dimensionSide,
        isTotal,
        sectionViews3d: sectionViews ? sectionViews["3d"] : null,
        dimensionableAxes: dimensionables.map((dz) => ({
          id: dz.id,
          visibleAxes: dz.visibleAxes,
        })),
        frameExternalInfo: frames.map((f) => ({
          id: f.id,
          posX: f.position?.[0],
          isExterior: f.isExterior,
        })),
        dimensions: computed.map((d) => ({
          id: d.id,
          heightMM: d.heightMM,
          widthMM: d.widthMM,
          showHeight: d.showHeight,
          showWidth: d.showWidth,
          dimensionSide: d.dimensionSide,
          positions: d.positions,
        })),
      });
    }
  }, [frames, dimensionSide]);

  return (
    <group userData={{ type: "dimensions" }}>
      {dims.map((d) => (
        <Fragment key={d.id}>
          {d.showLength && <DimensionAxis axis="length" data={d} />}
          {d.showHeight && <DimensionAxis axis="height" data={d} />}
          {d.showWidth && <DimensionAxis axis="width" data={d} />}
        </Fragment>
      ))}
    </group>
  );
}

function DimensionObjects({ position, rotation, length }) {
  const dimensionRadius = 0.005;
  return (
    <group position={position} rotation={rotation}>
      <mesh>
        <cylinderGeometry args={[dimensionRadius, dimensionRadius, length]} />
        <meshStandardMaterial color={"Black"} />
      </mesh>
      <mesh position={[0, -length / 2, 0]} rotation={[0, 0, -Math.PI / 2]}>
        <cylinderGeometry args={[dimensionRadius, dimensionRadius, 0.1]} />
        <meshStandardMaterial color={"Black"} />
      </mesh>
      <mesh position={[0, length / 2, 0]} rotation={[0, 0, -Math.PI / 2]}>
        <cylinderGeometry args={[dimensionRadius, dimensionRadius, 0.1]} />
        <meshStandardMaterial color={"Black"} />
      </mesh>
    </group>
  );
}
